import { getSetting, getSettingLabel } from '@koob/utils';
import ExportFlowPage from './ExportFlowPage';

const CATEGORY_ORDER = ['hotels', 'experiences', 'additionalServices'];

const CATEGORY_SLUG = {
  hotels: 'pricing-category-hotels',
  experiences: 'pricing-category-experiences',
  additionalServices: 'pricing-category-additional-services',
};

const MODE_SLUG = {
  global: 'pricing-mode-global',
  categories: 'pricing-mode-categories',
  items: 'pricing-mode-items',
  daily: 'pricing-mode-daily',
};

function formatAmount(amount, currency) {
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: currency || 'EUR',
    maximumFractionDigits: 0,
  }).format(amount ?? 0);
}

function HeadlineCard({ pricing, labels, intro }) {
  return (
    <div className="flex flex-col items-start justify-between gap-4 rounded-lg bg-[var(--td-ink)] p-8 text-white md:flex-row md:gap-8">
      <div>
        <div className="text-2xs font-semibold tracking-widest uppercase text-[var(--td-primary)]">
          {[
            getSettingLabel(labels, 'pricing-per-person'),
            '·',
            getSettingLabel(labels, 'pricing-based-on'),
            pricing.travellerCount,
            getSettingLabel(labels, 'pricing-travellers'),
          ].join(' ')}
        </div>
        <div
          className="mt-3 text-5xl"
          style={{ fontFamily: 'var(--td-font-display)' }}
        >
          {formatAmount(pricing.perPersonTotal, pricing.currency)}
        </div>
      </div>

      {intro && (
        <div className="max-w-xs text-xs opacity-70 md:flex-shrink-0 md:self-end">
          {intro}
        </div>
      )}
    </div>
  );
}

function CategoriesBreakdown({ pricing, labels }) {
  const categories = CATEGORY_ORDER.map((key) =>
    pricing.categories?.find((c) => c.key === key),
  ).filter(Boolean);
  const total =
    pricing.perPersonTotal ||
    categories.reduce((sum, c) => sum + (c.perPersonAmount ?? 0), 0);

  return (
    <div className="mt-6 grid grid-cols-3 gap-4">
      {categories.map((category) => (
        <div
          key={category.key}
          className="border border-k-gray/40 rounded-lg p-4"
        >
          <div className="text-2xs font-semibold tracking-widest uppercase text-[var(--td-primary)]">
            {getSettingLabel(labels, CATEGORY_SLUG[category.key])}
          </div>
          <div className="mt-1 text-lg font-medium">
            {formatAmount(category.perPersonAmount, pricing.currency)}
          </div>
          <div className="mt-2 h-1.5 bg-k-gray/40 rounded-full overflow-hidden">
            <div
              className="h-full bg-[var(--td-primary)] rounded-full"
              style={{
                width: `${
                  total > 0
                    ? ((category.perPersonAmount ?? 0) / total) * 100
                    : 0
                }%`,
              }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

function ItemsBreakdown({ pricing, labels }) {
  const visibleCategories = CATEGORY_ORDER.map((key) => {
    const items = pricing.items?.filter((item) => item.category === key);
    if (!items?.length) return null;
    const subtotal = items.reduce((sum, item) => sum + (item.amount ?? 0), 0);
    return { key, items, subtotal };
  }).filter(Boolean);

  const totalRow = (
    <div className="flex items-center justify-between border-t-2 border-[var(--td-ink)] pt-3">
      <div className="text-sm font-semibold uppercase tracking-wide">
        {[
          getSettingLabel(labels, 'pricing-total'),
          '·',
          pricing.travellerCount,
          getSettingLabel(labels, 'pricing-travellers'),
        ].join(' ')}
      </div>
      <div
        className="text-2xl"
        style={{ fontFamily: 'var(--td-font-display)' }}
      >
        {formatAmount(pricing.grandTotal, pricing.currency)}
      </div>
    </div>
  );

  return (
    <div className="mt-6">
      {visibleCategories.map(({ key, items, subtotal }, index) => {
        const category = (
          <div className="mb-6">
            <div className="text-2xs font-semibold tracking-widest uppercase text-[var(--td-primary)]">
              {getSettingLabel(labels, CATEGORY_SLUG[key])}
            </div>
            <div className="mt-2 space-y-2">
              {items.map((item, itemIndex) => (
                <div
                  key={itemIndex}
                  className="flex items-center justify-between text-sm border-b border-k-gray/30 pb-2"
                >
                  <div className="flex-1">{item.label}</div>
                  <div className="w-40 text-[var(--td-ink-muted)] text-xs">
                    {item.startDate}
                    {item.endDate && item.endDate !== item.startDate
                      ? ` – ${item.endDate}`
                      : ''}
                  </div>
                  <div className="w-10 text-[var(--td-ink-muted)] text-xs">
                    ×{item.pax}
                  </div>
                  <div className="w-24 text-right font-medium">
                    {formatAmount(item.amount, pricing.currency)}
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-1 text-right text-xs text-[var(--td-ink-muted)] uppercase tracking-wide">
              {getSettingLabel(labels, 'pricing-subtotal')} ·{' '}
              {formatAmount(subtotal, pricing.currency)}
            </div>
          </div>
        );

        return index === visibleCategories.length - 1 ? (
          <div key={key} data-prevent-cut>
            {category}
            {totalRow}
          </div>
        ) : (
          <div key={key}>{category}</div>
        );
      })}

      {visibleCategories.length === 0 && totalRow}
    </div>
  );
}

export default function ExportPricing({
  trip,
  tourOperator,
  pricing,
  settings,
}) {
  const labels = getSetting(settings, 'pricing')?.sections;

  if (!pricing) return null;

  const modeSlug = MODE_SLUG[pricing.mode] ?? MODE_SLUG.categories;

  return (
    <ExportFlowPage
      sectionLabel={getSetting(settings, 'pricing')?.displayName}
      title={getSettingLabel(labels, 'pricing-title')}
      badge={getSettingLabel(labels, `${modeSlug}-badge`)}
      trip={trip}
      tourOperator={tourOperator}
      settings={settings}
      footerStatus
    >
      <div>
        <div className="mt-2">
          <HeadlineCard
            pricing={pricing}
            labels={labels}
            intro={getSettingLabel(labels, `${modeSlug}-intro`)}
          />
        </div>

        {pricing.mode === 'categories' && (
          <CategoriesBreakdown pricing={pricing} labels={labels} />
        )}

        {pricing.mode === 'items' && (
          <ItemsBreakdown pricing={pricing} labels={labels} />
        )}

        {pricing.mode === 'daily' && (
          <div className="mt-6 text-sm italic text-[var(--td-ink-muted)]">
            {getSettingLabel(labels, 'pricing-daily-notice')}
          </div>
        )}
      </div>
    </ExportFlowPage>
  );
}
